import { useState } from 'react';
import { generateType as generateTypeAPI } from '../../sources/type';
import translations from '../../translations';
import type { GeneratedTypeSetAPI } from '../../types/type';
import { isNullOrEmpty } from '../../utils/guard';
import { stringReplace } from '../../utils/string';
import ButtonDefault from '../buttons/ButtonDefault';
import ButtonPrimary from '../buttons/ButtonPrimary';
import CodeEditor from '../generic/CodeEditor';
import Footer from '../generic/Footer';
import FormGroup from '../generic/FormGroup';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import './generate-type.css';
import GeneratedTypesPreview from './GeneratedTypesPreview';
import { useTypes } from './TypesProvider';

const LANGUAGES = [
    { value: 'json', label: 'JSON' },
    { value: 'xml', label: 'XML' },
] as const;

type Language = (typeof LANGUAGES)[number]['value'];

const isValidContent = (value: string, language: Language) => {
    try {
        if (language === 'json') {
            JSON.parse(value);
            return true;
        }

        const document = new DOMParser().parseFromString(value, 'application/xml');
        return document.getElementsByTagName('parsererror').length === 0;
    } catch {
        return false;
    }
};

const GenerateType = () => {
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [language, setLanguage] = useState<Language>('json');
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const [isGenerating, setIsGenerating] = useState(false);
    const [preview, setPreview] = useState<GeneratedTypeSetAPI | null>(null);

    const { returnToTypeList } = useTypes();
    const dispatchNotification = useNotificationDispatch();

    const languageLabel = LANGUAGES.find((option) => option.value === language)?.label ?? '';

    const onGenerate = async () => {
        setHasSubmitted(true);

        if (isNullOrEmpty(name.trim())) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: translations.TYPES_name_validation_message,
                },
            });

            return;
        }

        if (isNullOrEmpty(content.trim())) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: translations.TYPES_generate_type_empty_content,
                },
            });

            return;
        }

        if (!isValidContent(content, language)) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error: stringReplace(translations.TYPES_generate_type_invalid_content, {
                        language: languageLabel,
                    }),
                },
            });

            return;
        }

        try {
            setIsGenerating(true);
            const generated = await generateTypeAPI({ developerName: name, content, language });
            setPreview(generated);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const isNameInvalid = hasSubmitted && isNullOrEmpty(name.trim());

    const isContentInvalid =
        hasSubmitted && (isNullOrEmpty(content.trim()) || !isValidContent(content, language));

    if (preview) {
        return <GeneratedTypesPreview set={preview} onBack={() => setPreview(null)} />;
    }

    return (
        <>
            <div className="admin-page">
                <h1>{translations.TYPES_generateType}</h1>
                <p>{translations.TYPES_generate_type_description}</p>
                <FormGroup
                    label={translations.COMMON_name}
                    htmlFor="generate-type-name"
                    isRequired
                    validationMessage={translations.TYPES_name_validation_message}
                    isValid={!isNameInvalid}
                    showValidation={hasSubmitted}
                >
                    <input
                        id="generate-type-name"
                        className="form-control form-control-long"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        type="text"
                        placeholder={translations.TYPES_name_input_placeholder}
                        autoFocus
                    />
                </FormGroup>
                <FormGroup
                    label={translations.TYPES_generate_type_content_label}
                    htmlFor="generate-type-content"
                    isRequired
                    validationMessage={stringReplace(
                        translations.TYPES_generate_type_invalid_content,
                        { language: languageLabel },
                    )}
                    isValid={!isContentInvalid}
                    showValidation={hasSubmitted}
                    className="generate-type-editor"
                >
                    <div className="generate-type-editor-wrapper">
                        <select
                            className="generate-type-language"
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as Language)}
                            aria-label={translations.TYPES_generate_type_language_label}
                            title={translations.TYPES_generate_type_language_label}
                        >
                            {LANGUAGES.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <CodeEditor
                            name="generate-type-content"
                            mode={language}
                            value={content}
                            onChange={(value) => setContent(value)}
                            placeholder={translations.TYPES_generate_type_content_placeholder}
                            height="500px"
                            highlightActiveLine
                        />
                    </div>
                </FormGroup>
            </div>
            <Footer>
                <ButtonDefault
                    title={translations.COMMON_back}
                    className="flex-child-right"
                    onClick={returnToTypeList}
                    disabled={isGenerating}
                >
                    {translations.COMMON_back}
                </ButtonDefault>
                <ButtonPrimary
                    title={translations.TYPES_generate_type_button_label}
                    onClick={onGenerate}
                    disabled={isGenerating}
                >
                    {translations.TYPES_generate_type_button_label}
                </ButtonPrimary>
            </Footer>
        </>
    );
};

export default GenerateType;
